import { createRef, Component } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { withTranslation } from 'react-i18next'
import { detect } from 'detect-browser'

import Grid from 'beam/dist/Grid'
import ExpandableDrawer from './ExpandableDrawer'
import IconButton from 'beam/dist/IconButton'
import BeamIcon from 'beam/dist/BeamIcon'
import withStyles from 'beam/dist/styles'

import MediaPlayerHeader from './MediaPlayerHeader'
import MediaPlayerWorkspace from './MediaPlayerWorkspace'
import RestoreMediaDialog from './RestoreMediaDialog'
import { ROLE_ANALYST, ROLE_EVALUATOR } from './mediaPlayer.constants'

import ErrorBoundary from '../ErrorBoundary'
import { MEDIA_DISPLAY_STATE } from '../const/mediaDisplayState'
import Snackbar from '../../../Common/NguiSnackbar'

import { authenticatedUserShape, withAuthenticatedUser } from '../../../Permissions'

export const MEDIA_PLAYER_WIDTH_UNITS = 80

const EVENT_LISTENER_MESSAGE_CHANNEL = 'C1MediaPlayer'

const PHASE = {
  PAUSED: 0,
  SOFT_TICK: 1,
  VIDEO: 2,
  AUDIO: 3,
}

const SOFT_TICK_TIME = 300

const styles = theme => ({
  root: {
    display: 'flex',
  },
  panels: {
    flex: '1 1 auto',
    justifyContent: 'center',
    margin: theme.spacing(0),
    width: '100%',
    height: 'calc(100% - 64px)',
    backgroundColor: theme.palette.grey[300],
    zIndex: 0,
    flexWrap: 'nowrap',
    columnGap: theme.spacing(2),
    '& > div': {
      padding: theme.spacing(0),
      height: '100%',
    },
  },
  drawerPaper: {
    overflow: 'hidden',
    width: theme.spacing(MEDIA_PLAYER_WIDTH_UNITS),
    height: `calc(100vh - ${theme.spacing(8)}px)`, // TODO: move the header + footer height into the calabrio theme
    boxShadow: theme.shadows[6],
    position: 'absolute',
  },
  drawerPaperExpanded: {
    width: '100%',
  },
  snackbarError: {
    backgroundColor: theme.palette.error.main,
  },
})

export const dataWrapperPropType = PropTypes.shape({
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}).isRequired

class MediaPlayer extends Component {
  static propTypes = {
    /** ccrid, current selected call */
    ccrid: PropTypes.number,
    /** Audio URL */
    storageFile: dataWrapperPropType,
    /** computed media timeline */
    mediaTimeline: PropTypes.object.isRequired,
    /** Timezone override (null if none) */
    timezoneOverride: PropTypes.string,
    media: PropTypes.shape({
      main: PropTypes.object.isRequired,
      /** speech hits */
      speech: dataWrapperPropType,
      /** events */
      events: dataWrapperPropType,
      /** energy */
      energy: dataWrapperPropType,
      transcription: PropTypes.object,
      desktop: PropTypes.object,
      chat: PropTypes.object,
    }),
    /** Callback for when Media Player closes */
    onClose: PropTypes.func.isRequired,
    /** Callback for when Media Player is mounted in DOM */
    getContact: PropTypes.func.isRequired,
    /** Callback for opening a new contact from next and previous buttons */
    openContact: PropTypes.func.isRequired,
    /** Passed in by react-i18next */
    t: PropTypes.func.isRequired,
    /** Passed in by withAuthenticatedUser */
    authenticatedUser: authenticatedUserShape.isRequired,
    nextAndPreviousContactIds: PropTypes.object,
    role: PropTypes.string,
    isExpanded: PropTypes.bool.isRequired,
    toggleMediaPlayerCollapsed: PropTypes.func.isRequired,
    saveMediaPlayerRole: PropTypes.func.isRequired,
    restoreStorageFiles: PropTypes.func.isRequired,
    delayedStorageStatus: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    hasDx: PropTypes.bool,
    mediaFormats: PropTypes.object.isRequired,
    updateContact: PropTypes.func.isRequired,
    goalId: PropTypes.number,
    hasReviewGoal: PropTypes.bool,
    completeReviewGoal: PropTypes.func.isRequired,
    saveArchiveAudit: PropTypes.func.isRequired,
    saveAudioPlayedAudit: PropTypes.func.isRequired,
    onMediaPlayerClose: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    scopeType: PropTypes.string,
    showExpandButton: PropTypes.bool,
  }

  static defaultProps = {
    ccrid: null,
    storageFile: {},
    nextAndPreviousContactIds: {
      next: null,
      previous: null,
    },
    role: ROLE_EVALUATOR,
    hasDx: false,
    timezoneOverride: false,
    scopeType: null,
    goalId: null,
    hasReviewGoal: false,
  }

  state = {
    audioReady: false,
    videoReady: false,
    mediaPlaying: false,
    playbackInterrupted: false,
    mediaCurrentTime: 0,
    mediaPlaybackRate: 1,
    audioLoaded: false,
    hasAudioData: null,
    hasVideoData: null,
    videoHasErrors: false,
    zoomed: false,
    mediaRestoreAcknowledged: false,
    audioUrls: [],
    reviewGoalComplete: false,
    isPopupBlockerWarningOpen: false,
    autoQAAIAnalysisHasData: true,
    autoQAAutoEvaluationHasData: true,
    autoQAReloadTimestamp: 0,
    autoQAFormId: 0,
    advSentimentTrendGraphHasData: true,
  }

  roles = [ROLE_ANALYST, ROLE_EVALUATOR]
  audioEl = createRef()
  browser = detect()

  componentDidMount() {
    this.loadContact()
    this.setAudioUrls()

    this.setState({
      audioReady: false,
      videoReady: false,
      mediaPlaying: false,
      mediaCurrentTime: 0,
      audioLoaded: false,
      hasAudioData: null,
      hasVideoData: null,
      videoHasErrors: false,
      reviewGoalComplete: false,
      isPopupBlockerWarningOpen: false,
      autoQAAIAnalysisHasData: true,
      autoQAAutoEvaluationHasData: true,
      autoQAReloadTimestamp: 0,
      autoQAFormId: 0,
      advSentimentTrendGraphHasData: true,
    })

    window.addEventListener('message', this.mediaPlayerMessageListener)
  }

  componentDidUpdate(prevProps) {
    const { hasAudioData, hasVideoData } = this.state

    if (prevProps.ccrid !== this.props.ccrid || prevProps.scopeType !== this.props.scopeType) {
      this.loadContact()
      const { mediaPlaying, mediaCurrentTime } = this.state

      if (this.audioEl.current && mediaPlaying && this.inAudioPhase(mediaCurrentTime)) {
        this.audioEl.current.pause()
      }
      this.stopSoftTick()
      this.setCallTime(0)
      this.setState({
        audioReady: false,
        videoReady: false,
        mediaPlaying: false,
        mediaCurrentTime: 0,
        audioLoaded: false,
        hasAudioData: null,
        hasVideoDate: null,
        videoHasErrors: false,
        reviewGoalComplete: false,
        autoQAAIAnalysisHasData: true,
        autoQAAutoEvaluationHasData: true,
        autoQAReloadTimestamp: 0,
        autoQAFormId: 0,
        advSentimentTrendGraphHasData: true,
      })
    }
    if (
      prevProps.mediaFormats.audio !== this.props.mediaFormats.audio ||
      prevProps.media.main.storageFile.data.audio !== this.props.media.main.storageFile.data.audio
    ) {
      this.setAudioUrls()
    }
    if (prevProps.goalId !== this.props.goalId) {
      this.setState({
        reviewGoalComplete: false,
      })
    }

    if (hasAudioData === null) {
      this.checkAudioData()
    }

    if (hasVideoData === null) {
      this.checkVideoData()
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.mediaPlayerMessageListener)

    const {
      media: {
        main: {
          contact: { data },
        },
      },
      onMediaPlayerClose,
    } = this.props
    onMediaPlayerClose(data)
  }

  mediaPlayerMessageListener = event => {
    if (event.data.channel === EVENT_LISTENER_MESSAGE_CHANNEL) {
      if (event.data.action === 'AutoQA.AIAnalysis.enable') {
        this.setState({ autoQAAIAnalysisHasData: event.data.hasData })
      } else if (event.data.action === 'AutoQA.AutoEvaluation.enable') {
        this.setState({ autoQAAutoEvaluationHasData: event.data.hasData })
      } else if (event.data.action === 'AutoQA.AIAnalysis.reload' && event.data.formId !== null) {
        this.setState({ autoQAFormId: event.data.formId, autoQAReloadTimestamp: Date.now() })
      } else if (event.data.action === 'AutoQA.Transcription.setMessageTime' && event.data.time !== null) {
        this.setCallTime(event.data.time)
      } else if (event.data.action === 'AdvSentiment.trendGraph.enable') {
        this.setState({ advSentimentTrendGraphHasData: event.data.hasData })
      } else if (event.data.action === 'AdvSentiment.Transcription.setMessageTime' && event.data.time !== null) {
        this.setCallTime(event.data.time)
      }
    }
  }

  loadContact = () => {
    const { getContact, ccrid, scopeType } = this.props

    getContact(ccrid, scopeType)
  }

  toggleExpand = () => {
    this.props.toggleMediaPlayerCollapsed()
  }

  /**
   * Every call goes through different phases, the phase is determined by what media
   * is available at a point in time.
   * If audio data is available, then it's the audio phase.
   * If there's no audio, but there is video, then it's the video phase.
   * If there's no audio or video, then it's the soft-tick phase.
   *
   * The 'soft-tick' is an artificial tick introduced to move time forward when there is
   * neither audio nor video.
   *
   * There's also a pseudo-phase, the paused phase.
   * This phase isn't determined by a point in time (except at the end of the call),
   * rather it is determined solely by user action.
   *

 call                                                                      call
 start                                                                     end
   |----------------------░░░░░░░AUDIO░░░░░░░░------------------------------|
   |------------▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓VIDEO▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓---------------------|
         △              △            △            △            △            △
         |              |            |            |            |            |
         |              |            |            |            |            |
     soft-tick        video        audio        video      soft-tick     paused
       phase          phase        phase        phase        phase        phase

  */

  inAudioPhase = time => {
    const { audioStart, audioEnd } = this.props.mediaTimeline

    return this.hasAudio() && time >= audioStart && time < audioEnd
  }

  inVideoPhase = time => {
    const { videoStart, videoEnd } = this.props.mediaTimeline
    const { videoHasErrors } = this.state

    return !this.inAudioPhase(time) && this.hasVideo() && !videoHasErrors && time >= videoStart && time < videoEnd
  }

  isEndOfCall = time => {
    const { audioEnd, videoEnd, duration } = this.props.mediaTimeline

    return time >= Math.max(audioEnd, videoEnd, duration)
  }

  getPhaseForTime = time => {
    if (this.inAudioPhase(time)) {
      return PHASE.AUDIO
    } else if (this.inVideoPhase(time)) {
      return PHASE.VIDEO
    } else if (this.isEndOfCall(time)) {
      return PHASE.PAUSED
    } else {
      return PHASE.SOFT_TICK
    }
  }

  loadAudio = () => {
    if (!this.state.audioLoaded) {
      /**
       * The audio element caches the audio files so we have to load new audio files, otherwise it would play the
       * previous cached audio file.
       */
      this.audioEl.current && this.audioEl.current.load()
      this.setState({ audioLoaded: true })
    }
  }

  setAudioTime = newTime => {
    const { audioStart, audioStorageFileOffset } = this.props.mediaTimeline

    const timeSetter = () => {
      if (this.audioEl.current && !isNaN(this.audioEl.current.duration)) {
        /**
         * In case contact is a reconciled associated contact--in which it shares
         * the same audio source--we need to set the audio time to the contact's
         * relative time mark
         */
        this.audioEl.current.currentTime = (newTime - audioStart + audioStorageFileOffset) / 1000
      }
      // Make sure to only run the listener once, if we can run this the data is loaded for this contact
      this.audioEl.current && this.audioEl.current.removeEventListener('loadeddata', timeSetter)
    }
    /**
     * If we have audio and it is ready, set the current time, otherwise wait for it to become ready, then set the time.
     * This is needed for reconciled recordings
     * When we first hit the play button the audio might not be ready yet
     * and it will just start playing at 0 instead of being set to the right storage file offset
     */
    if (this.audioEl.current) {
      if (this.audioEl.current.readyState > 0) {
        timeSetter()
      } else {
        this.audioEl.current.addEventListener('loadeddata', timeSetter)
      }
    }
  }

  playAudio = newTime => {
    this.loadAudio()

    this.setAudioTime(newTime)

    this.audioEl.current && this.audioEl.current.play()
  }

  getSoftTickTimeoutDuration = mediaCurrentTime => {
    const { audioStart, videoStart, duration } = this.props.mediaTimeline
    const { videoHasErrors } = this.state

    // Jump to the next soft-tick or the start of some media, whichever comes first
    if ((this.hasAudio() && mediaCurrentTime < audioStart) || (this.hasVideo() && mediaCurrentTime < videoStart)) {
      const minAudioVideoStart = Math.min.apply(
        null,
        [this.hasAudio() ? audioStart : null, this.hasVideo() && !videoHasErrors ? videoStart : null].filter(
          time => time !== null,
        ),
      )
      return Math.min(minAudioVideoStart - mediaCurrentTime, SOFT_TICK_TIME)
    } else {
      return Math.min(SOFT_TICK_TIME, duration - mediaCurrentTime)
    }
  }

  startSoftTick = mediaCurrentTime => {
    const timeout = this.getSoftTickTimeoutDuration(mediaCurrentTime)
    this.softTickTimeout = setTimeout(this.handleSoftTick, timeout, timeout)
  }

  stopSoftTick = () => {
    clearTimeout(this.softTickTimeout)
  }

  /**
   * When transitioning from phase to phase, media and/or soft-ticks
   * will need to be started/stopped. The overall playing/paused state
   * may need to be updated as well.
   */
  handlePhaseChange = (prevPhase, newPhase, time) => {
    if (newPhase === PHASE.SOFT_TICK) {
      // Soft-ticks don't play continuously, so we need to start a new one
      this.startSoftTick(time)
    } else if (prevPhase === PHASE.SOFT_TICK) {
      this.stopSoftTick()
    }

    /**
     * Other than soft-ticks, if we aren't changing phases
     * there's nothing to do.
     */
    if (prevPhase === newPhase) {
      return
    }

    if (newPhase === PHASE.AUDIO) {
      this.playAudio(time)
    } else if (prevPhase === PHASE.AUDIO) {
      this.audioEl.current && this.audioEl.current.pause()
    }

    if (newPhase === PHASE.PAUSED) {
      this.setState({
        mediaPlaying: false,
      })
    } else if (prevPhase === PHASE.PAUSED) {
      this.setState({
        mediaPlaying: true,
      })
    }
  }

  playCall = () => {
    const {
      ccrid,
      media: {
        main: {
          contact: {
            data: { isRootRecording },
          },
        },
      },
      saveAudioPlayedAudit,
    } = this.props

    const { mediaPlaying, mediaCurrentTime } = this.state

    if (!mediaPlaying) {
      const newPhase = this.getPhaseForTime(mediaCurrentTime)
      this.handlePhaseChange(PHASE.PAUSED, newPhase, mediaCurrentTime)

      // Don't record this playback if we are at the end of the call
      if (newPhase !== PHASE.PAUSED) {
        saveAudioPlayedAudit(ccrid, isRootRecording)
      }
    }
  }

  pauseCall = () => {
    const { mediaPlaying, mediaCurrentTime } = this.state

    if (mediaPlaying) {
      const currentPhase = this.getPhaseForTime(mediaCurrentTime)
      this.handlePhaseChange(currentPhase, PHASE.PAUSED, mediaCurrentTime)
    }
  }

  handlePlayPauseButtonClick = () => {
    this.state.mediaPlaying ? this.pauseCall() : this.playCall()
  }

  /**
   * setCallTime updates the Media Player time based on a user interaction
   * like clicking a transcription bubble or desktop event
   */
  setCallTime = newTime => {
    const { mediaPlaying, mediaCurrentTime } = this.state

    if (mediaPlaying) {
      const currentPhase = this.getPhaseForTime(mediaCurrentTime)
      const newPhase = this.getPhaseForTime(newTime)

      if (currentPhase === PHASE.AUDIO && newPhase === PHASE.AUDIO) {
        /**
         * If we're playing audio and not changing phases, we'll need to manually
         * set the audio time
         */
        this.setAudioTime(newTime)
      } else {
        this.handlePhaseChange(currentPhase, newPhase, newTime)
      }
    }

    this.setState({ mediaCurrentTime: newTime })
  }

  // handleTimeUpdate updates the Media Player time based on media playback
  handleTimeUpdate = (newTime, currentPhase) => {
    const {
      ccrid,
      goalId,
      hasReviewGoal,
      authenticatedUser: { user },
      completeReviewGoal,
    } = this.props
    const { mediaCurrentTime, reviewGoalComplete } = this.state

    const newPhase = this.getPhaseForTime(newTime)
    this.handlePhaseChange(currentPhase, newPhase, newTime)

    if (hasReviewGoal && !reviewGoalComplete && this.hasMetReviewGoal(newTime)) {
      completeReviewGoal(ccrid, goalId, user.id)
      this.setState({
        reviewGoalComplete: true,
      })
    }

    if (
      currentPhase === PHASE.AUDIO &&
      newPhase === PHASE.AUDIO &&
      Math.abs(newTime - mediaCurrentTime) < 1000
    ) {
      // IE cannot keep up with re-rendering much more than once a second, so we are throttling when playing audio
      return
    }

    this.setState({ mediaCurrentTime: newTime })
  }

  handleTimeUpdateFromAudio = event => {
    const { mediaPlaying, mediaCurrentTime, playbackInterrupted } = this.state

    if (mediaPlaying && !playbackInterrupted) {
      const { audioStart, audioStorageFileOffset } = this.props.mediaTimeline

      if (this.inAudioPhase(mediaCurrentTime)) {
        const mediaNewTime = Math.max(0, event.target.currentTime * 1000 + audioStart - audioStorageFileOffset)

        this.handleTimeUpdate(mediaNewTime, PHASE.AUDIO)
      }
    }
  }

  handleTimeUpdateFromVideo = videoTime => {
    const { mediaPlaying, mediaCurrentTime, playbackInterrupted } = this.state

    if (mediaPlaying && !playbackInterrupted) {
      const { videoStart } = this.props.mediaTimeline

      if (this.inVideoPhase(mediaCurrentTime)) {
        const mediaNewTime = videoTime + videoStart

        this.handleTimeUpdate(mediaNewTime, PHASE.VIDEO)
      }
    }
  }

  handleSoftTick = timeout => {
    const { mediaPlaying, mediaCurrentTime, playbackInterrupted } = this.state

    if (mediaPlaying && !playbackInterrupted) {
      const mediaNewTime = mediaCurrentTime + timeout

      this.handleTimeUpdate(mediaNewTime, PHASE.SOFT_TICK)
    }
  }

  hasMetReviewGoal = newTime => {
    const {
      mediaTimeline: { duration },
      media: {
        main: {
          contact: {
            data: { goal },
          },
        },
      },
    } = this.props
    const { mediaPlaying } = this.state

    if (mediaPlaying || this.isEndOfCall(newTime)) {
      const goalCompletionTime = duration * (goal.percentToComplete / 100)

      if (newTime >= goalCompletionTime) {
        return true
      }
    }

    return false
  }

  isMediaReady = () => {
    const {
      media: {
        main: { contact },
      },
    } = this.props
    const { audioReady, videoReady, videoHasErrors } = this.state

    const hasVideo = contact.data && this.hasVideo() && !videoHasErrors
    const hasAudio = contact.data && this.hasAudio()

    if (hasVideo && hasAudio) {
      return audioReady && videoReady
    } else if (hasVideo) {
      return videoReady
    } else {
      return audioReady
    }
  }

  handlePrevClick = () => {
    const {
      openContact,
      nextAndPreviousContactIds: { previous },
    } = this.props

    this.pauseCall()

    if (previous) {
      openContact(previous)
    }
  }

  handleNextClick = () => {
    const {
      openContact,
      nextAndPreviousContactIds: { next },
    } = this.props

    this.pauseCall()

    if (next) {
      openContact(next)
    }
  }

  handlePlaybackRateChange = event => {
    const mediaPlaybackRate = event.target.value
    if (this.audioEl.current) {
      this.audioEl.current.playbackRate = mediaPlaybackRate
    }
    this.setState({ mediaPlaybackRate })
  }

  toggleZoom = () => {
    this.setState(prevState => ({ zoomed: !prevState.zoomed }))
  }

  handleGetMediaCurrentTime = () => this.state.mediaCurrentTime

  handleMarkForCalabrationClick = () => {}

  handleRoleChange = role => this.props.saveMediaPlayerRole(role)

  handleScrubStart = () => {
    if (this.state.mediaPlaying) {
      this.setState(
        {
          playbackInterrupted: true,
        },
        this.pauseCall(),
      )
    }
  }

  handleScrubEnd = newTime => {
    const { playbackInterrupted } = this.state
    if (playbackInterrupted) {
      this.setState(
        {
          playbackInterrupted: false,
          mediaCurrentTime: newTime,
        },
        () => this.playCall(),
      )
    } else {
      this.setCallTime(newTime)
    }
  }

  handleAudioCanPlay = () => {
    this.loadAudio()
    this.setState({ audioReady: true })
  }

  handleVideoCanPlay = () => {
    this.setState({ videoReady: true })
  }

  handlePopupsBlocked = () => {
    this.setState({ isPopupBlockerWarningOpen: true })
  }
  hidePopupBlockerWarning = () => this.setState({ isPopupBlockerWarningOpen: false })

  handleMediaRestore = () => {
    const { restoreStorageFiles } = this.props

    restoreStorageFiles()
  }

  handleRestoreMediaDialogClose = () => {
    this.setState({
      mediaRestoreAcknowledged: true,
    })
  }

  videoRestoreDeclined = () => this.props.delayedStorageStatus.video && this.state.mediaRestoreAcknowledged
  audioRestoreDeclined = () => this.props.delayedStorageStatus.audio && this.state.mediaRestoreAcknowledged

  hasVideo = () => {
    const { contact } = this.props.media.main
    const { hasVideoData } = this.state

    return (
      hasVideoData !== false &&
      !this.videoRestoreDeclined() &&
      !isEmpty(contact.data) &&
      contact.data.screenDisplayStateId === MEDIA_DISPLAY_STATE.UPLOADED
    )
  }

  videoUploadFailed = () => {
    const uploadError = this.props.media.main.storageFile.error
    const screenDisplayState = this.props.media.main.contact.data.screenDisplayStateId
    // restore acknowledged, storage file error, and uploadPending all must be true
    return (
      this.state.mediaRestoreAcknowledged &&
      uploadError &&
      screenDisplayState === MEDIA_DISPLAY_STATE.PENDING_UPLOAD
    )
  }

  hasAudio = () => {
    const { contact } = this.props.media.main
    const { hasAudioData } = this.state

    return (
      hasAudioData &&
      !this.audioRestoreDeclined() &&
      !isEmpty(contact.data) &&
      contact.data.audioDisplayStateId === MEDIA_DISPLAY_STATE.UPLOADED
    )
  }

  checkAudioData = () => {
    const {
      media: {
        main: { storageFile },
      },
    } = this.props

    if (storageFile.loading || !storageFile.data) {
      return
    }

    const {
      data: { audio },
    } = storageFile

    const hasSomeAudio = Object.values(audio).some(fileType => fileType.length > 0)

    this.setState({ hasAudioData: hasSomeAudio })
  }

  checkVideoData = () => {
    const {
      media: {
        main: { storageFile },
      },
    } = this.props

    if (storageFile.loading || !storageFile.data) {
      return
    }

    const {
      data: { video },
    } = storageFile

    const hasSomeVideo = Object.values(video).some(fileType => fileType.length > 0)

    this.setState({ hasVideoData: hasSomeVideo })
  }

  getAudioURLs = audio =>
    audio
      ? Object.keys(audio)
        .filter(format => format === this.props.mediaFormats.audio)
        .reduce((audioURLs, audioKey) => {
          if (audio[audioKey][0] && audio[audioKey][0].status === 'available') {
            return [
              ...audioURLs,
              {
                url: audio[audioKey][0].url,
                type: audioKey,
              },
            ]
          } else {
            return audioURLs
          }
        }, [])
      : []

  setAudioUrls = () => {
    const { storageFile } = this.props.media.main
    const audioUrls = this.getAudioURLs(storageFile.data.audio)

    this.setState({
      audioUrls,
    })
  }

  /* Callback that updates state when ~all~ available screen recordings fail to load */
  handleVideoHasErrors = () => {
    this.setState({ videoHasErrors: true })
  }

  renderHeader = () => {
    const {
      media: {
        main: { contact },
      },
      mediaTimeline,
      ccrid,
      onClose,
      role,
      timezoneOverride,
      nextAndPreviousContactIds,
      isExpanded,
      updateContact,
      showExpandButton,
    } = this.props

    const { mediaPlaying, mediaPlaybackRate, mediaCurrentTime, zoomed } = this.state

    return (
      <MediaPlayerHeader
        ccrid={ccrid}
        contact={contact}
        isExpanded={isExpanded}
        isZoomed={zoomed}
        isMediaPlaying={mediaPlaying}
        isMediaReady={this.isMediaReady()}
        mediaCurrentTime={mediaCurrentTime}
        mediaTimeline={mediaTimeline}
        timezoneOverride={timezoneOverride}
        mediaPlaybackRate={mediaPlaybackRate}
        role={role}
        nextAndPreviousContactIds={nextAndPreviousContactIds}
        onToggleMediaPlaying={this.handlePlayPauseButtonClick}
        onToggleZoom={this.toggleZoom}
        onToggleExpand={this.toggleExpand}
        onRoleChange={this.handleRoleChange}
        onPrevClick={this.handlePrevClick}
        onNextClick={this.handleNextClick}
        onClose={onClose}
        onPlaybackRateChange={this.handlePlaybackRateChange}
        onUpdateContact={updateContact}
        showExpandButton={showExpandButton}
      />
    )
  }

  renderWorkspace = () => {
    const {
      media,
      mediaTimeline,
      ccrid,
      openContact,
      isExpanded,
      goalId,
    } = this.props

    const { mediaPlaybackRate, mediaCurrentTime, hasAudioData, hasVideoData, mediaPlaying, zoomed } = this.state

    return (
      <MediaPlayerWorkspace
        ccrid={ccrid}
        hasVideo={this.hasVideo()}
        media={media}
        mediaTimeline={mediaTimeline}
        isMediaPlaying={mediaPlaying}
        isZoomed={zoomed}
        isExpanded={isExpanded}
        mediaCurrentTime={mediaCurrentTime}
        hasAudioData={hasAudioData}
        hasVideoData={hasVideoData}
        onVideoHasErrors={this.handleVideoHasErrors}
        mediaPlaybackRate={mediaPlaybackRate}
        videoRestoreDeclined={this.videoRestoreDeclined()}
        onPopupsBlocked={this.handlePopupsBlocked}
        onTimeUpdateFromVideo={this.handleTimeUpdateFromVideo}
        onSetTime={this.setCallTime}
        onOpenContact={openContact}
        onVideoCanPlay={this.handleVideoCanPlay}
        onScrubStart={this.handleScrubStart}
        onScrubEnd={this.handleScrubEnd}
        onGetMediaCurrentTime={this.handleGetMediaCurrentTime}
        videoUploadFailed={this.videoUploadFailed()}
        goalId={goalId}
        autoQAAIAnalysisHasData={this.state.autoQAAIAnalysisHasData}
        autoQAAutoEvaluationHasData={this.state.autoQAAutoEvaluationHasData}
        autoQAReloadTimestamp={this.state.autoQAReloadTimestamp}
        autoQAFormId={this.state.autoQAFormId}
        advSentimentTrendGraphHasData={this.state.advSentimentTrendGraphHasData}
      />
    )
  }

  render() {
    const {
      t,
      classes,
      ccrid,
      media: {
        main: { contact },
      },
      delayedStorageStatus,
      isExpanded,
    } = this.props

    const { hasAudioData, hasVideoData, audioUrls, mediaRestoreAcknowledged, isPopupBlockerWarningOpen } = this.state

    const showRestoreMediaDialog = Object.keys(delayedStorageStatus).some(media => delayedStorageStatus[media])

    if (hasAudioData === null || hasVideoData === null) {
      return null
    }

    return (
      <ExpandableDrawer
        classes={{
          drawerPaper: classes.drawerPaper,
          drawerPaperExpanded: classes.drawerPaperExpanded,
        }}
        open
        expanded={isExpanded}
        anchor="right"
      >
        <ErrorBoundary key={ccrid}>
          <Snackbar
            open={isPopupBlockerWarningOpen}
            onClose={this.hidePopupBlockerWarning}
            ContentProps={{
              className: classes.snackbarError,
            }}
            message={t('popupBlockerDockingWarning')}
            action={
              <IconButton key="close" aria-label={t('close')} color="inherit" onClick={this.hidePopupBlockerWarning}>
                <BeamIcon>Close</BeamIcon>
              </IconButton>
            }
          />
          {this.renderHeader()}
          {audioUrls.length > 0 && (
            <div>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification */}
              <audio
                src={audioUrls[0].url}
                key={contact.id}
                ref={this.audioEl}
                onTimeUpdate={this.handleTimeUpdateFromAudio}
                onCanPlay={this.handleAudioCanPlay}
              />
            </div>
          )}
        </ErrorBoundary>
        <Grid container className={classes.panels}>
          {this.renderWorkspace()}
        </Grid>
        {showRestoreMediaDialog && (
          <RestoreMediaDialog
            open={showRestoreMediaDialog && !mediaRestoreAcknowledged}
            delayedStorageStatus={delayedStorageStatus}
            onRestore={this.handleMediaRestore}
            onClose={this.handleRestoreMediaDialogClose}
          />
        )}
      </ExpandableDrawer>
    )
  }
}

export { MediaPlayer as UnstyledMediaPlayer }
export default withStyles(styles, { withTheme: true })(withTranslation()(withAuthenticatedUser(MediaPlayer)))
